import { Fragment, type ReactNode } from 'react';
import type { LegalContentBlock, LegalTextRun } from '@/lib/legal-content';
import { cn } from '@/lib/utils';

interface LegalContentProps {
  blocks: LegalContentBlock[];
}

function renderTextWithLineBreaks(text: string, keyPrefix: string) {
  return text.split('\n').map((line, lineIndex) => (
    <Fragment key={`${keyPrefix}-${lineIndex}`}>
      {lineIndex > 0 && <br />}
      {line}
    </Fragment>
  ));
}

function renderTextRun(run: LegalTextRun, keyPrefix: string) {
  const className = cn(
    run.bold && 'font-semibold',
    run.italic && 'italic',
    run.underline && 'underline',
    run.href && 'text-blush-600 underline underline-offset-2 hover:text-blush-700'
  );
  const content = renderTextWithLineBreaks(run.text, keyPrefix);

  if (run.href) {
    const isExternal = run.href.startsWith('http');
    return (
      <a
        key={keyPrefix}
        href={run.href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noreferrer' : undefined}
        className={className}
      >
        {content}
      </a>
    );
  }

  if (className) {
    return (
      <span key={keyPrefix} className={className}>
        {content}
      </span>
    );
  }

  return <Fragment key={keyPrefix}>{content}</Fragment>;
}

function renderRuns(runs: LegalTextRun[], keyPrefix: string) {
  return runs.map((run, runIndex) => renderTextRun(run, `${keyPrefix}-run-${runIndex}`));
}

function renderBlock(
  block: Exclude<LegalContentBlock, { type: 'list-item' | 'spacer' }>,
  index: number
) {
  const content = renderRuns(block.runs, `legal-block-${index}`);

  switch (block.type) {
    case 'title':
      return (
        <h1
          key={index}
          className="font-display text-3xl font-semibold leading-tight text-gray-900 sm:text-4xl"
        >
          {content}
        </h1>
      );
    case 'date':
      return (
        <p key={index} className="font-body text-sm italic leading-6 text-gray-600">
          {content}
        </p>
      );
    case 'heading':
      return (
        <h2
          key={index}
          className="pt-5 font-display text-xl font-semibold leading-8 text-gray-900 sm:text-2xl"
        >
          {content}
        </h2>
      );
    case 'subheading':
      return (
        <h3 key={index} className="pt-2 font-display text-lg font-semibold leading-7 text-gray-900">
          {content}
        </h3>
      );
    case 'paragraph':
      return (
        <p key={index} className="whitespace-pre-wrap font-body text-base leading-7 text-gray-700">
          {content}
        </p>
      );
  }
}

export function LegalContent({ blocks }: LegalContentProps) {
  const content: ReactNode[] = [];

  for (let blockIndex = 0; blockIndex < blocks.length; blockIndex += 1) {
    const block = blocks[blockIndex];

    if (block.type === 'spacer') {
      content.push(<div key={blockIndex} aria-hidden="true" className="h-2" />);
      continue;
    }

    if (block.type !== 'list-item') {
      content.push(renderBlock(block, blockIndex));
      continue;
    }

    const listItems: Array<{
      block: Extract<LegalContentBlock, { type: 'list-item' }>;
      index: number;
    }> = [];
    let listIndex = blockIndex;

    while (listIndex < blocks.length && blocks[listIndex].type === 'list-item') {
      listItems.push({
        block: blocks[listIndex] as Extract<LegalContentBlock, { type: 'list-item' }>,
        index: listIndex,
      });
      listIndex += 1;
    }

    content.push(
      <ul
        key={`legal-list-${blockIndex}`}
        className="space-y-2 pl-5 font-body text-base leading-7 text-gray-700"
      >
        {listItems.map(({ block: listItem, index }) => (
          <li
            key={index}
            className={cn(
              'list-disc whitespace-pre-wrap pl-1 marker:text-gray-500',
              listItem.level === 2 && 'ml-6 list-[circle]'
            )}
          >
            {renderRuns(listItem.runs, `legal-block-${index}`)}
          </li>
        ))}
      </ul>
    );

    blockIndex = listIndex - 1;
  }

  return <div className="space-y-5">{content}</div>;
}
